import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Upload, DollarSign, Tag, FileText, Image as ImageIcon, 
  Save, Eye, EyeOff, Loader2, Trash2, AlertCircle, Plus
} from 'lucide-react';
import { CATEGORIES } from '../../constants/serviceConstants';
import { ServicesService, ServiceStatus } from '../../services/servicesService';

export const ServiceEditor = ({ 
  isOpen, 
  onClose, 
  providerId, 
  service = null,
  onSave
}) => {
  const isEditing = !!service;
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    category: '',
    priceStart: '',
    tags: [],
    imageUrl: '',
    status: ServiceStatus.DRAFT,
  });
  
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (service) {
      setFormData({
        title: service.title || '',
        description: service.description || '',
        categoryId: service.categoryId || '',
        category: service.category || '',
        priceStart: service.priceStart?.toString() || '',
        tags: service.tags || [],
        imageUrl: service.imageUrl || '',
        status: service.status || ServiceStatus.DRAFT,
      });
      setImagePreview(service.imageUrl || '');
    } else {
      resetForm();
    }
  }, [service, isOpen]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      categoryId: '',
      category: '',
      priceStart: '',
      tags: [],
      imageUrl: '',
      status: ServiceStatus.DRAFT,
    });
    setImagePreview('');
    setTagInput('');
    setImageFile(null);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const category = CATEGORIES.find(c => c.id === categoryId);
    setFormData(prev => ({ 
      ...prev, 
      categoryId,
      category: category?.name || ''
    }));
    if (errors.categoryId) setErrors(prev => ({ ...prev, categoryId: null }));
  };

  const handleImageChange = (file) => {
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 5MB' }));
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please select an image file' }));
      return;
    }
    
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, image: null }));
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    handleImageChange(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleImageChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 10) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ 
      ...prev, 
      tags: prev.tags.filter(t => t !== tagToRemove) 
    }));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length > 100) newErrors.title = 'Title must be less than 100 characters';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 2000) newErrors.description = 'Description must be less than 2000 characters';
    if (!formData.categoryId) newErrors.categoryId = 'Category is required';
    if (!formData.priceStart || parseFloat(formData.priceStart) <= 0) {
      newErrors.priceStart = 'Valid price is required';
    }
    if (parseFloat(formData.priceStart) > 100000) {
      newErrors.priceStart = 'Price cannot exceed R100,000';
    }
    if (!imagePreview && !formData.imageUrl) {
      newErrors.image = 'Service image is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (status = formData.status) => {
    if (!validate()) return;
    
    setIsLoading(true);
    try {
      let imageUrl = formData.imageUrl;
      const serviceId = service?.id || `service_${Date.now()}`;
      
      // Upload new image if selected
      if (imageFile) {
        imageUrl = await ServicesService.uploadImage(providerId, serviceId, imageFile);
      }

      const serviceData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        categoryId: formData.categoryId,
        category: formData.category,
        priceStart: parseFloat(formData.priceStart),
        tags: formData.tags,
        imageUrl,
        status,
      };

      let savedService;
      if (isEditing) {
        savedService = await ServicesService.update(service.id, serviceData);
        savedService = { ...service, ...serviceData, id: service.id };
      } else {
        savedService = await ServicesService.create(providerId, serviceData);
      }

      onSave?.(savedService);
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to save service. Please try again.' }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-100 max-h-[90vh] flex flex-col">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
            <div>
              <h3 className="text-xl font-bold text-white">
                {isEditing ? 'Edit Service' : 'Create New Service'}
              </h3>
              <p className="text-indigo-100 text-sm mt-0.5">
                {isEditing ? 'Update your service details' : 'Add a new service to your catalog'}
              </p>
            </div>
            <button 
              onClick={onClose} 
              disabled={isLoading}
              className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>

          {/* Form */}
          <div className="overflow-y-auto flex-1 p-6">
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{errors.submit}</span>
              </div>
            )}

            <div className="space-y-6">
              
              {/* Row 1: Title & Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                    Service Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Professional Logo Design"
                    maxLength={100}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${
                      errors.title ? 'border-red-300' : 'border-slate-200'
                    }`}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
                    <p className="text-slate-400 text-xs ml-auto">{formData.title.length}/100</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                    Category *
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleCategoryChange}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${
                      errors.categoryId ? 'border-red-300' : 'border-slate-200'
                    }`}
                  >
                    <option value="">Select a category</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.categoryId && <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>}
                </div>
              </div>

              {/* Row 2: Price & Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                    Starting Price (USD) *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="number"
                      name="priceStart"
                      value={formData.priceStart}
                      onChange={handleChange}
                      min="1"
                      max="100000"
                      step="0.01"
                      placeholder="0.00"
                      className={`w-full pl-9 pr-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${
                        errors.priceStart ? 'border-red-300' : 'border-slate-200'
                      }`}
                    />
                  </div>
                  {errors.priceStart && <p className="text-red-500 text-xs mt-1">{errors.priceStart}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                    Status
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, status: ServiceStatus.DRAFT }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                        formData.status === ServiceStatus.DRAFT
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <EyeOff className="w-4 h-4" />
                      Draft
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, status: ServiceStatus.PUBLISHED }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                        formData.status === ServiceStatus.PUBLISHED
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      Published
                    </button>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  Description *
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    maxLength={2000}
                    placeholder="Describe your service, what's included, deliverables, and what makes it unique..."
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none ${
                      errors.description ? 'border-red-300' : 'border-slate-200'
                    }`}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
                  <p className="text-slate-400 text-xs ml-auto">{formData.description.length}/2000</p>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  Tags (up to 10) - Help buyers find your service
                </label>
                <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                  {formData.tags.map(tag => (
                    <span key={tag} className="pl-3 pr-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold flex items-center gap-1 border border-indigo-100">
                      {tag}
                      <button 
                        type="button"
                        onClick={() => removeTag(tag)} 
                        className="hover:text-red-600 p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {formData.tags.length === 0 && (
                    <span className="text-xs text-slate-400 italic">No tags added yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Type a tag and press Enter"
                      maxLength={30}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addTag}
                    disabled={!tagInput.trim() || formData.tags.length >= 10}
                    className="px-4 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  Service Image *
                </label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                    isDragging 
                      ? 'border-indigo-400 bg-indigo-50' 
                      : errors.image 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-slate-200 hover:border-indigo-300 bg-slate-50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="max-h-48 rounded-lg shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center gap-3">
                      <div className="p-4 bg-white rounded-full shadow-sm">
                        <ImageIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <div>
                        <span className="text-sm font-bold text-indigo-600">Click to upload</span>
                        <span className="text-sm text-slate-500"> or drag and drop</span>
                      </div>
                      <p className="text-xs text-slate-400">PNG, JPG, WEBP up to 5MB (Recommended: 800x600px)</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileInput}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3 justify-end rounded-b-2xl flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(ServiceStatus.DRAFT)}
              disabled={isLoading}
              className="px-6 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save as Draft
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(ServiceStatus.PUBLISHED)}
              disabled={isLoading}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              {isEditing ? 'Update & Publish' : 'Publish Service'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEditor;